import { AtlasAITool } from '@common/interfaces/atlasai'
import { Feedback } from '@common/interfaces/prospects'
import Base from './base'

export { AtlasAITool }

export default class AtlasAI extends Base {
  // Kill one detached background subagent, or all active ones (omit subagentId).
  cancelSubagent(sessionId: string, subagentId?: string) {
    return this.apiClient.post(
      `atlasai/sessions/${sessionId}/subagents/cancel`,
      subagentId ? { subagentId } : {},
    )
  }

  // Stop the session's multi-step plan so it stops continuing on its own.
  abandonPlan(sessionId: string) {
    return this.apiClient.post(`atlasai/sessions/${sessionId}/plan/abandon`, {})
  }

  getAttachmentEmbedLink(boxFileId: string) {
    return this.apiClient.get(`atlasai/attachments/${boxFileId}/embed-link`)
  }

  submitFeedback(data: { messageId: string; feedbackType: Feedback; comment?: string }) {
    return this.apiClient.post('atlasai/feedback', data)
  }

  // ── Inviteable users ──────────────────────────────────────────────────────

  listInviteableUsers(params: object = {}) {
    return this.apiClient.get('atlasai/inviteable-users', params)
  }

  // ── Approvals ─────────────────────────────────────────────────────────────

  listApprovals(params: { sessionId?: string; taskId?: string } = {}) {
    return this.apiClient.get('atlasai/approvals', params)
  }

  approveApproval(approvalId: string, data: object = {}) {
    return this.apiClient.post(`atlasai/approvals/${approvalId}/approve`, data)
  }

  rejectApproval(approvalId: string) {
    return this.apiClient.post(`atlasai/approvals/${approvalId}/reject`, {})
  }

  // ── Docs ──────────────────────────────────────────────────────────────────

  listDocs(params: object = {}) {
    return this.apiClient.get('atlasai/docs', params)
  }

  getDoc(id: string) {
    return this.apiClient.get(`atlasai/docs/${id}`)
  }

  addDoc(data: object) {
    return this.apiClient.post('atlasai/docs', data)
  }

  updateDoc(id: string, data: object) {
    return this.apiClient.put(`atlasai/docs/${id}`, data)
  }

  deleteDoc(id: string) {
    return this.apiClient.delete(`atlasai/docs/${id}`)
  }

  reingestDoc(id: string) {
    return this.apiClient.post(`atlasai/docs/${id}/reingest`, {})
  }

  // ── Memories ──────────────────────────────────────────────────────────────

  listMemories(params: object = {}) {
    return this.apiClient.get('atlasai/memories', params)
  }

  getMemory(id: string) {
    return this.apiClient.get(`atlasai/memories/${id}`)
  }

  addMemory(data: object) {
    return this.apiClient.post('atlasai/memories', data)
  }

  updateMemory(id: string, data: object) {
    return this.apiClient.put(`atlasai/memories/${id}`, data)
  }

  deleteMemory(id: string) {
    return this.apiClient.delete(`atlasai/memories/${id}`)
  }

  resolveMemoryFlags(id: string) {
    return this.apiClient.post(`atlasai/memories/${id}/resolve-flags`, {})
  }

  // ── Prompts ──────────────────────────────────────────────────────────────

  listPrompts(params: object = {}) {
    return this.apiClient.get('atlasai/prompts', params)
  }

  getPrompt(id: string) {
    return this.apiClient.get(`atlasai/prompts/${id}`)
  }

  addPrompt(data: object) {
    return this.apiClient.post('atlasai/prompts', data)
  }

  updatePrompt(id: string, data: object) {
    return this.apiClient.put(`atlasai/prompts/${id}`, data)
  }

  deletePrompt(id: string) {
    return this.apiClient.delete(`atlasai/prompts/${id}`)
  }

  // ── Workflows (read-only) ────────────────────────────────────────────────

  listWorkflows(params: object = {}) {
    return this.apiClient.get('atlasai/workflows', params)
  }

  // ── GIFs (read-only, GIPHY-backed) ───────────────────────────────────────

  searchGifs(query?: string) {
    return this.apiClient.get('atlasai/gifs', query ? { query } : {})
  }

  // ── Automations ──────────────────────────────────────────────────────────

  listAutomations(params: object = {}) {
    return this.apiClient.get('atlasai/automations', params)
  }

  getAutomation(id: string, params: object = {}) {
    return this.apiClient.get(`atlasai/automations/${id}`, params)
  }

  createAutomation(data: object) {
    return this.apiClient.post('atlasai/automations', data)
  }

  updateAutomation(id: string, data: object) {
    return this.apiClient.put(`atlasai/automations/${id}`, data)
  }

  deleteAutomation(id: string) {
    return this.apiClient.delete(`atlasai/automations/${id}`)
  }
}
